// DTO del detalle del Dashboard directivo — gráfico central + costo de paros
// + ranking de pérdida (rediseño 2026-07).
//
//   - GET /api/directivo/resumen-detalle?desde&hasta&granularidad[&turno]
//
// Granularidades (decisión 2026-07-04):
//   - 'hora'      → buckets por hora del día (vista Hoy)
//   - 'dia'       → buckets por fecha
//   - 'semana'    → buckets por semana ISO
//   - 'hora_dia'  → perfil horario: colapsa los días del rango por franja
//                   (el FE divide produccion/dias_con_dato para el PROMEDIO)
// 'hora' y 'hora_dia' agrupan igual en el backend (hora del bloque); difieren
// en cómo el FE presenta (suma del día vs promedio por franja).
//
// Reglas de honestidad del dato:
//   - El programa cliente es DIARIO y sin turno → programa_unds/real_planif_unds
//     solo vienen en granularidad dia/semana SIN filtro de turno; si no, null.
//   - Paros en $ = VALOR NO PRODUCIDO (fórmula C6, precio vigente); null sin
//     puede_ver_costos.
//
// Todo viene desglosado POR PLANTA: el filtro de planta del FE no refetchea.
import { z } from 'zod';
export const granularidadDetalleSchema = z.enum(['hora', 'dia', 'semana', 'hora_dia']);
export const directivoResumenDetalleQuerySchema = z
    .object({
    desde: z.string().date(),
    hasta: z.string().date(),
    granularidad: granularidadDetalleSchema,
    turno: z.coerce.number().int().min(1).max(3).optional(),
})
    .refine((q) => q.desde <= q.hasta, { message: 'desde debe ser <= hasta' });
/** Punto de la serie central: (bucket × planta). */
export const seriePuntoSchema = z.object({
    bucket: z.string(),
    planta_id: z.string().uuid(),
    produccion: z.number(),
    meta_capacidad_unds: z.number(),
    /** Días distintos con datos en el bucket (divisor del promedio en hora_dia). */
    dias_con_dato: z.number(),
    /** Solo dia/semana sin turno; null cuando el programa no aplica al corte. */
    programa_unds: z.number().nullable(),
    real_planif_unds: z.number().nullable(),
});
/** Paros por causal madre en el bucket (para el stacked del costo de paros). */
export const paroCausalPuntoSchema = z.object({
    bucket: z.string(),
    planta_id: z.string().uuid(),
    causal: z.string(),
    /** Color hex de la categoría de la causal madre (config del tenant, A3). */
    color: z.string().nullable(),
    minutos: z.number(),
    unds_perdidas: z.number(),
    valor_cop: z.number().nullable(),
});
/** Fila del ranking de pérdida por línea (el FE toma top-5 tras filtrar planta). */
export const perdidaLineaSchema = z.object({
    linea_id: z.string().uuid(),
    codigo: z.string(),
    planta_id: z.string().uuid(),
    minutos: z.number(),
    unds_perdidas: z.number(),
    valor_cop: z.number().nullable(),
});
/** Nivel de servicio por bucket × planta (para la serie "Cumplimiento REAL"
 *  del gráfico central). Solo dia/semana sin turno (las OPs son diarias y sin
 *  turno); atribución por fecha PROGRAMADA de la OP. */
export const nivelRealPuntoSchema = z.object({
    bucket: z.string(),
    planta_id: z.string().uuid(),
    programadas: z.number(),
    realizadas: z.number(),
    no_entregadas: z.number(),
});
export const directivoResumenDetalleSchema = z.object({
    rango: z.object({ desde: z.string().date(), hasta: z.string().date() }),
    granularidad: granularidadDetalleSchema,
    turno: z.number().int().nullable(),
    puede_ver_costos: z.boolean(),
    serie: z.array(seriePuntoSchema),
    /** null cuando no aplica (granularidad horaria o filtro de turno). */
    nivel_real: z.array(nivelRealPuntoSchema).nullable(),
    paros_causal: z.array(paroCausalPuntoSchema),
    perdida_lineas: z.array(perdidaLineaSchema),
    /** DN1: paros ABIERTOS del rango (eventos distintos) — aparte, no suman. */
    paros_en_curso: z.number().int().min(0),
    actualizado_at: z.string(),
});
//# sourceMappingURL=directivo_resumen_detalle.js.map